// src/components/ToastProvider.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';

import { ToastContext } from '../hooks/useToast';

function createToast(message, options = {}) {
  const id = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return {
    id,
    message,
    severity: options.severity || 'info',
    autoHideDuration: options.autoHideDuration ?? 4200,
  };
}

export default function ToastProvider({ children }) {
  const [queue, setQueue] = useState([]);
  const [activeToast, setActiveToast] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (activeToast || queue.length === 0) return;

    setActiveToast(queue[0]);
    setQueue((prev) => prev.slice(1));
    setOpen(true);
  }, [activeToast, queue]);

  const show = useCallback((message, options = {}) => {
    if (!message) return;

    const nextToast = createToast(message, options);

    // For high-frequency actions such as chip selections, replace the active
    // toast instead of queueing many near-identical messages.
    if (options.replace) {
      setQueue([]);
      setActiveToast(nextToast);
      setOpen(true);
      return;
    }

    setQueue((prev) => [...prev, nextToast]);
  }, []);

  const value = useMemo(
    () => ({
      show,
      success: (message, options = {}) => show(message, { ...options, severity: 'success' }),
      error: (message, options = {}) => show(message, { ...options, severity: 'error' }),
      warning: (message, options = {}) => show(message, { ...options, severity: 'warning' }),
      info: (message, options = {}) => show(message, { ...options, severity: 'info' }),
    }),
    [show]
  );

  const handleClose = (_event, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const handleExited = () => {
    setActiveToast(null);
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={open}
        onClose={handleClose}
        autoHideDuration={activeToast?.autoHideDuration}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionProps={{ onExited: handleExited }}
      >
        {activeToast ? (
          <Alert
            onClose={handleClose}
            severity={activeToast.severity}
            variant="filled"
            sx={{
              width: '100%',
              borderRadius: 1.5,
              boxShadow: '0 10px 30px rgba(15, 23, 42, 0.22)',
              fontWeight: 700,
            }}
          >
            {activeToast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </ToastContext.Provider>
  );
}
